import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Stethoscope, Search, ArrowUpRight } from "lucide-react";

export default function FindDoctorCTA() {
  return (
    <section
      id="find-a-dermatologist-cta"
      data-testid="find-derm-cta-section"
      className="relative overflow-hidden py-24 md:py-32 bg-[color:var(--relumia-ink)] text-[color:var(--relumia-base)]"
    >
      {/* Ambient gold wash */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-1/4 h-[420px] w-[420px] rounded-full bg-[color:var(--relumia-gold)]/12 blur-[120px]" />
        <div className="absolute -right-32 -bottom-32 h-[520px] w-[520px] rounded-full bg-[color:var(--relumia-gold)]/8 blur-[140px]" />
        {/* subtle grid */}
        <div className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(197,160,89,.4) 1px,transparent 1px),linear-gradient(90deg,rgba(197,160,89,.4) 1px,transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-20 items-center">
        {/* Left: message */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="flex items-center gap-3 mb-5">
            <Stethoscope size={18} className="text-gold" strokeWidth={1.6} />
            <span className="overline !text-gold">Consult a Specialist</span>
          </div>

          <h2 className="font-serif font-light tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-[64px]">
            Find a Dermatologist
            <br />
            <span className="italic text-gold">near you.</span>
          </h2>

          <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-white/70">
            Relumia works best when guided by a qualified skin specialist. Explore our
            directory of{" "}
            <span className="text-gold">1,400+ verified dermatologists</span> across
            Mumbai, Pune, Nagpur, Ranchi, Patna, Kolhapur and beyond — searchable by
            city, area or pincode.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link
              to="/find-a-dermatologist"
              data-testid="home-find-derm-cta"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[color:var(--relumia-gold)] px-8 py-4 text-[13px] tracking-wide text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-gold-deep)] hover:text-white transition-colors"
            >
              <MapPin size={16} strokeWidth={2} />
              Find a Dermatologist
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
            </Link>
            <Link
              to="/skin-analysis"
              data-testid="home-find-derm-assessment"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-[13px] tracking-wide text-white hover:border-[color:var(--relumia-gold)] hover:text-gold transition-colors"
            >
              Take Clinical Assessment first
            </Link>
          </div>

          {/* Stats row */}
          <div className="mt-14 grid grid-cols-3 gap-6 md:gap-10 max-w-lg">
            {[
              ["1,400+", "Verified specialists"],
              ["16", "Indian cities"],
              ["6 lakh+", "Pincodes covered"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-serif text-3xl md:text-[38px] text-gold leading-none">{n}</div>
                <div className="mt-2 text-[11.5px] tracking-widest uppercase text-white/50">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right: search preview card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="relative"
        >
          <div className="relative rounded-sm border border-white/10 bg-white/[0.03] backdrop-blur-xl p-6 md:p-8">
            <div className="flex items-center gap-2 mb-5">
              <span className="h-2 w-2 rounded-full bg-[color:var(--relumia-gold)] animate-pulse" />
              <span className="overline !text-white/60">Directory Preview</span>
            </div>

            <div className="flex items-center gap-3 rounded-full border border-white/15 bg-black/30 px-5 py-3.5">
              <Search size={16} className="text-white/40" strokeWidth={1.8} />
              <span className="text-[13px] text-white/50 flex-1">Search city, area or pincode…</span>
              <span className="text-[10.5px] tracking-widest uppercase text-gold/70">e.g. 400068</span>
            </div>

            {/* Mock result rows */}
            <div className="mt-6 space-y-3">
              {[
                { d: "Dr. Manisha Savani", c: "Satwik Hair & Skin Care", a: "Mira Road East, Mumbai · 401107" },
                { d: "Dr. Aditi Deshpande", c: "Renee Skin Hair Laser & Aesthetic", a: "Mulund, Mumbai · 400080" },
                { d: "Dr. Angela Nagpal", c: "Skin Wellness Clinic", a: "Ghatkopar, Mumbai · 400077" },
              ].map((r, i) => (
                <div key={r.d} className="flex items-start gap-3 rounded-sm border border-white/8 bg-white/[0.02] p-4 hover:border-[color:var(--relumia-gold)]/40 transition-colors">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[color:var(--relumia-gold)]/40 text-gold">
                    <MapPin size={13} strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-[16px] text-white truncate">{r.d}</div>
                    <div className="text-[12px] text-white/60 truncate">{r.c}</div>
                    <div className="text-[11px] text-white/40 mt-0.5 truncate">{r.a}</div>
                  </div>
                  <span className="hidden md:inline text-[10.5px] tracking-widest uppercase text-gold/60 shrink-0 mt-1">Verified</span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-5 border-t border-white/10 text-[11.5px] text-white/50">
              …and 1,400+ more specialists. Tap to explore →
            </div>
          </div>

          {/* Decorative frame */}
          <div className="pointer-events-none absolute -inset-2 -z-10 rounded-sm border border-[color:var(--relumia-gold)]/12" />
        </motion.div>
      </div>
    </section>
  );
}
